import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import QuickBooksSourceBanner from './QuickBooksSourceBanner';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  IconButton,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SettingsIcon from '@mui/icons-material/Settings';
import quickbooksService, {
  formatCurrency,
  formatCompactCurrency,
  formatDate,
  getAgingColor,
  getEntityColor,
} from '../../services/quickbooksService';

export default function FinanceDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [overview, setOverview] = useState(null);
  const [cashPosition, setCashPosition] = useState(null);
  const [arAging, setArAging] = useState(null);
  const [apAging, setApAging] = useState(null);
  const [intercompanyBalances, setIntercompanyBalances] = useState([]);
  const [reconciliationSummary, setReconciliationSummary] = useState(null);

  useEffect(() => {
    loadData();
  }, [selectedEntity]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = selectedEntity !== 'all' ? { connector_id: selectedEntity } : {};

      // Load entities first
      const entitiesRes = await quickbooksService.getQuickBooksEntities();
      if (entitiesRes.success) {
        setEntities(entitiesRes.data);
      }

      // Load all dashboard data in parallel
      const [overviewRes, cashRes, arRes, apRes, icRes, reconRes] = await Promise.all([
        quickbooksService.getFinancialOverview(params),
        quickbooksService.getCashPosition(params),
        quickbooksService.getARAgingSummary(params),
        quickbooksService.getAPAgingSummary(params),
        quickbooksService.getIntercompanyBalances(),
        quickbooksService.getReconciliationSummary(params),
      ]);

      if (overviewRes.success) setOverview(overviewRes.data);
      if (cashRes.success) setCashPosition(cashRes.data);
      if (arRes.success) setArAging(arRes.data);
      if (apRes.success) setApAging(apRes.data);
      if (icRes.success) setIntercompanyBalances(icRes.data?.accounts || (Array.isArray(icRes.data) ? icRes.data : []));
      if (reconRes.success) setReconciliationSummary(reconRes.data);

    } catch (err) {
      console.error('Error loading finance data:', err);
      setError('Failed to load financial data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (event) => {
    setSelectedEntity(event.target.value);
  };

  const KPICard = ({ title, value, subtitle, icon, color = 'primary', trend, onClick }) => (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color: `${color}.main` }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            backgroundColor: `${color}.light`,
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            {icon}
          </Box>
        </Box>
        {trend !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            {trend >= 0 ? (
              <TrendingUpIcon fontSize="small" color="success" />
            ) : (
              <TrendingDownIcon fontSize="small" color="error" />
            )}
            <Typography
              variant="body2"
              color={trend >= 0 ? 'success.main' : 'error.main'}
              sx={{ ml: 0.5 }}
            >
              {Math.abs(trend).toFixed(1)}% vs last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const totalAR = arAging?.total || 0;
  const totalAP = apAging?.total || 0;
  const totalCash = cashPosition?.total || 0;

  return (
    <Box sx={{ p: 3 }}>
      <QuickBooksSourceBanner />
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Finance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Multi-entity financial overview from QuickBooks
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Entity</InputLabel>
            <Select
              value={selectedEntity}
              label="Entity"
              onChange={handleEntityChange}
            >
              <MenuItem value="all">All Entities</MenuItem>
              {entities.map((entity) => (
                <MenuItem key={entity.connector_id} value={entity.connector_id}>
                  {entity.company_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Configuration">
            <IconButton onClick={() => navigate('/finance/config/store-mappings')} color="primary">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh Data">
            <IconButton onClick={loadData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Total Cash Position"
            value={formatCompactCurrency(totalCash)}
            subtitle={`Across ${cashPosition?.all_accounts?.length || 0} accounts`}
            icon={<AccountBalanceIcon sx={{ color: 'primary.main' }} />}
            color="primary"
            onClick={() => navigate('/finance/cash')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Accounts Receivable"
            value={formatCompactCurrency(totalAR)}
            subtitle={`${arAging?.overdue_count || 0} invoices overdue`}
            icon={<ReceiptIcon sx={{ color: 'success.main' }} />}
            color="success"
            onClick={() => navigate('/finance/ar')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Accounts Payable"
            value={formatCompactCurrency(totalAP)}
            subtitle={`${apAging?.overdue_count || 0} bills overdue`}
            icon={<PaymentIcon sx={{ color: 'warning.main' }} />}
            color="warning"
            onClick={() => navigate('/finance/ap')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Intercompany Balance"
            value={formatCompactCurrency(intercompanyBalances.reduce((sum, ic) => sum + (ic.balance || ic.net_balance || 0), 0))}
            subtitle={`${intercompanyBalances.length} entity pairs`}
            icon={<CompareArrowsIcon sx={{ color: 'info.main' }} />}
            color="info"
            onClick={() => navigate('/finance/intercompany')}
          />
        </Grid>
      </Grid>

      {/* Reconciliation Alerts */}
      {reconciliationSummary && (reconciliationSummary.mismatches > 0 || reconciliationSummary.unmatched > 0) && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => navigate('/finance/reconciliation')}
            >
              <ArrowForwardIcon />
            </IconButton>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <WarningAmberIcon />
            <span>
              <strong>{reconciliationSummary.mismatches || 0}</strong> payment mismatches and{' '}
              <strong>{reconciliationSummary.unmatched || 0}</strong> unmatched transactions need attention
            </span>
          </Box>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* AR Aging Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  AR Aging Summary
                </Typography>
                <Chip
                  label="View Details"
                  size="small"
                  onClick={() => navigate('/finance/ar')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              {arAging?.buckets && (
                <Box>
                  {Object.entries(arAging.buckets).map(([bucket, amount]) => (
                    <Box key={bucket} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{bucket}</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(amount)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={totalAR > 0 ? (amount / totalAR) * 100 : 0}
                        color={getAgingColor(bucket.includes('90') ? 91 : bucket.includes('60') ? 61 : bucket.includes('30') ? 31 : 0)}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* AP Aging Summary */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  AP Aging Summary
                </Typography>
                <Chip
                  label="View Details"
                  size="small"
                  onClick={() => navigate('/finance/ap')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              {apAging?.buckets && (
                <Box>
                  {Object.entries(apAging.buckets).map(([bucket, amount]) => (
                    <Box key={bucket} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">{bucket}</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(amount)}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={totalAP > 0 ? (amount / totalAP) * 100 : 0}
                        color={getAgingColor(bucket.includes('90') ? 91 : bucket.includes('60') ? 61 : bucket.includes('30') ? 31 : 0)}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Entity Cash Positions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Cash by Entity
                </Typography>
                <Chip
                  label="View Details"
                  size="small"
                  onClick={() => navigate('/finance/cash')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell align="right">Cash Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cashPosition?.by_entity?.slice(0, 5).map((entity) => (
                      <TableRow key={entity.connector_id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: getEntityColor(entity.entity_name || entity.company_name)
                              }}
                            />
                            {entity.entity_name || entity.company_name || `Entity ${entity.connector_id}`}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatCurrency(entity.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Intercompany Balances */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>
                  Intercompany Accounts
                </Typography>
                <Chip
                  label="View Details"
                  size="small"
                  onClick={() => navigate('/finance/intercompany')}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {intercompanyBalances.slice(0, 5).map((ic, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip
                            label={ic.entity_name || '-'}
                            size="small"
                            sx={{
                              backgroundColor: getEntityColor(ic.entity_name),
                              color: 'white',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {ic.account_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={600}
                            color={(ic.balance || 0) >= 0 ? 'success.main' : 'error.main'}
                          >
                            {formatCurrency(ic.balance || 0)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {intercompanyBalances.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No intercompany accounts found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
